// Componentes nativos
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Label } from "recharts";
import Modal from "react-modal";

// Componentes personalizados
import Header from "../../Header";

// Ícones
import { IoMdArrowRoundBack } from "react-icons/io";
import { BsFillGearFill } from "react-icons/bs";

// Classes de controle e de armazenamento
import OffStorage, { DadosUnifacNorm } from "../Core/OffStorage";
import { UNIFAC } from "../Core/CoeficienteAtividade";
import Substancia from "../Core/Substancia";

// Tipagem intermediária para o gráfico
type DataGraphGama = {
    fracao: number,
    Gama: number
};

export default function DiagramaGama() {
    // Gerencia a navegação
    const nav = useHistory();
    function back() {
        nav.goBack();
    }

    // Cria a instância do banco offline
    const banco = new OffStorage();

    // Gerencia as substâncias cadastradas e selecionada
    const [substancias, setSubstancias] = useState<DadosUnifacNorm[]>([]);
    const [solvente, setSolvente] = useState("");
    const [soluto, setSoluto] = useState("");

    // Gerencia as informações do gráfico
    const [carregado, setCarregado] = useState(false);
    const [dataGraph, setDataGraph] = useState<DataGraphGama[]>([]);

    // Gerencia o modal, alerta e informações de configuração avançada
    const [alerta, setAlerta] = useState("");
    const [aberto, setAberto] = useState(false);
    const [temperatura, setTemperatura] = useState("298");

    // Função de inicialização
    function init() {
        const disponiveis = banco.getDadosUnifac();
        setSubstancias(disponiveis);
    }

    // Funções de seleção de soluto e solvente
    function selSolvente(event: React.ChangeEvent<HTMLSelectElement>) {
        setSolvente(event.target.value);
        setAlerta("");
    }

    function selSoluto(event: React.ChangeEvent<HTMLSelectElement>) {
        setSoluto(event.target.value);
        setAlerta("");
    }

    // Função que gerancia a alteração da temperatura
    function alterarTemperatura(event: React.ChangeEvent<HTMLInputElement>) {
        // Altera o valor do input
        setTemperatura(event.target.value);

        const valor = parseFloat(event.target.value.replace(",", "."));
        if(!isNaN(valor)) {
            if(valor > 0) {
                setTemperatura(valor.toString());
                setAlerta("");
            } else {
                setAlerta("Atenção: Não é possível calcular para o zero absoluto.");
            }
        } else {
            setAlerta("Atenção: Informe um valor válido para a temperatura do sistema.");
        }
    }

    // Busca dados a partir do nome
    function find(nome: string) {
        let dados = substancias[0];
        substancias.forEach(item => {
            if(item.substancia?.nome == nome) {
                dados = item;
            }
        });
        return dados;
    }

    // Função de renderização
    function render() {
        if(alerta != "") {
            setCarregado(false);
            return;
        }
        if(soluto == "" || solvente == "") {
            setAlerta("Atenção: Selecione corretamente os componentes do sistema nas listas.");
            setCarregado(false);
            return;
        }
        
        calcular();
    }

    // Função para calcular
    function calcular() {
        // Cria a lista para armazenar informações do gráfico
        const data: DataGraphGama[] = [];

        // Cria a classe de cálculo unifac
        const unifac = new UNIFAC();
        unifac.setTemperatura(parseFloat(temperatura));

        // Encontra os dados das substâncias envolvidas
        const dadosSolvente = find(solvente);
        const dadosSoluto = find(soluto);

        // Cria a instância das classes de substância
        const instanciaSolvente = new Substancia(String(dadosSolvente.substancia?.formula), String(dadosSolvente.substancia?.nome));
        const instanciaSoluto = new Substancia(String(dadosSoluto.substancia?.formula), String(dadosSoluto.substancia?.nome));

        // Define o passo e a variação
        let x0 = 0;
        const passo = 100;
        const delta = 1 / passo;

        // Faz o cálculo para cada fração
        for(let i = 0; i <= passo; i++) {
            unifac.reset().add({
                fracao: (1 - x0) - i * delta,
                substancia: instanciaSolvente,
                subgrupos: dadosSolvente.subgrupos
            }).add({
                fracao: x0 + i * delta,
                substancia: instanciaSoluto,
                subgrupos: dadosSoluto.subgrupos
            });

            let gamas = unifac.calcular();
            data.push({
                fracao: parseFloat((x0 + i * delta).toFixed(2)),
                Gama: gamas[1]
            })
        }

        // Altera a variável de estado
        setDataGraph(data);
        setCarregado(true);
    }

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        render();
    }, [solvente, soluto, temperatura]);

    return (
        <div className="section">
            <Header />

            <div className="content">
                <div className="container">

                    <div className="linha-titulo">
                        <h3>Coeficiente de Atividade - Diagramas</h3>

                        <div className="botao-icone" onClick={back}>
                            <IoMdArrowRoundBack size={20} />
                            <span>Voltar</span>
                        </div>
                    </div>

                    <div className="linha-nivel">
                        <span className="nivel"><strong>Nível</strong>: Avançado</span>
                    </div>

                    <p>Aqui você pode criar diagramas para verificar a variação do coeficiente de atividade em uma mistura binária. Escolha o solvente e a substância que vai acompanhar, logo em seguida o gráfico será criado.</p>

                    <p>Para visualizar no gráfico em 2D, esta página limita o uso de apenas duas substâncias. Utilize outras ferramentas disponíveis em Priore para composições não binárias. Como se trata de uma ferramenta preditiva, o modelo utilizado aqui é o UNIFAC.</p>

                    <div className="linha-config">
                        <div className="botao-config" onClick={() => setAberto(true)}>
                            <BsFillGearFill size={17} />
                            <span>Opções Avançadas</span>
                        </div>
                    </div>

                    <div className="grid-row">

                        <div className="coluna-2 pad-r">
                            <label className="label-select-ferramenta">Solvente</label>
                            <select className="select-ferramenta" onChange={selSolvente}>
                                <option value="">Escolha o solvente</option>
                                {substancias.map(item => (
                                    <option value={item.substancia?.nome}>{item.substancia?.nome}</option>
                                ))}
                            </select>
                        </div>

                        <div className="coluna-2 pad-l">
                            <label className="label-select-ferramenta">Soluto</label>
                            <select className="select-ferramenta" onChange={selSoluto}>
                                <option value="">Escolha a substância</option>
                                {substancias.map(item => (
                                    <option value={item.substancia?.nome}>{item.substancia?.nome}</option>
                                ))}
                            </select>
                        </div>

                    </div>

                    <div className="grid-row">
                        <p className="alerta-ferramenta">{alerta}</p>
                    </div>

                    {carregado && <div className="grafico-ferramenta">
                        <h4 className="label-titulo-grafico">Coeficiente de Atividade vs Fração Molar do Soluto</h4>

                        <ResponsiveContainer minWidth={350} height={300}>
                            <LineChart data={dataGraph} >
                                <XAxis dataKey="fracao" stroke="#1a1a1a">
                                    <Label value="Fração" offset={-8} position="bottom" fontFamily="Ubuntu" />
                                </XAxis>

                                <YAxis stroke="#1a1a1a">
                                    <Label value="Coeficiente de Atividade" fontFamily="Ubuntu" angle={-90} position="center" dx={-22} />
                                </YAxis>

                                <Tooltip />
                                <Line type="monotone" dataKey="Gama" stroke="#1a1a1a" dot={false}/>
                            </LineChart>
                        </ResponsiveContainer>
                    </div>}
                    
                </div>

            </div>

            <Modal ariaHideApp={false} isOpen={aberto} style={modalStyles} onRequestClose={() => setAberto(false)}>
                <div className="form-ferramenta">
                    <label>Temperatura (K)</label>
                    <input value={temperatura} type="number" onChange={alterarTemperatura} />
                </div>
                
                <div className="grid-row">
                    <p className="alerta-ferramenta">{alerta}</p>
                </div>
            </Modal>

        </div>
    );
}

const modalStyles = {
    overlay: {
        backgroundColor: "rgba(250,250,250,0.3)"
    },
    content: {
        backgroundColor: "#f0f0f5",
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        maxWidth: 600,
        width: "90%",
        borderRadius: 10
    }
};